<script lang="ts" setup>
import '../scss/FormHome.scss';
import { computed, onMounted, ref } from 'vue';
import Slider from './Slider.vue';
import { useRoute, useRouter } from 'vue-router';
import { validationRut } from '../../../../helpers/validationRUT';
import {
  getAssets,
  createAudits,
  findClient,
  calculatePriceForLanding,
  findClientByTributaryCode,
} from '../../../../repositories/landing.repository';
import storePolicy from '@store/policy';
import getSubDomainInitial from '../../../../helpers/getSubDomainInitial';
import getImageFlag from '../../../../helpers/getImageFlag';
import { getDolarChile } from '../../../../helpers/exchangeChile';
import { getTypeMoney } from '../../../../helpers/getTypeMoneyByCountry';
import { useToast } from '../../../../composables/useToast';
import { useI18n } from 'vue-i18n';
import CorredorRepository from '@repositories/corredor.repository';

const { t } = useI18n();
const store = storePolicy();
const subDomainInitial = ref<string>('');
const router = useRouter();
const route = useRoute();
const refForm = ref<any>(null);
const toogleIndemnizationLimit = ref<boolean>(false);
const corredorValid = ref<boolean>(false);
const corredorChecked = ref<boolean>(false);
const endPrice = ref<number>(0);
const errorResponse = ref<string | null>(null);
const indemnificationLimit = ref<number>(200000);
const dolarPrice = ref<number | null>(null);
const UFvalue = ref<number | null>(null);
const isButtonDisabled = ref<boolean>(false);
const isValidRUT = ref<boolean>(false);
const { $toast } = useToast();
const assetOptions = ref<any>([]);
const isCorredorInvalid = computed(
  () => corredorChecked.value && !corredorValid.value,
);
const isCorredorUnavailable = computed(
  () => !corredorChecked.value || !corredorValid.value,
);
const MULTISELECT_DISABLED_CLASS = 'multiselect--disabled';

const handleChangeInput = async (e: any) => {
  const key = e.input.key;
  
  if (key === 'rut') {
    handleOnSubmitRUT(e)
  }
};

const handleOnSubmitRUT = async (e: any) => {
  // 19876543-0 42.245.781-K
  isValidRUT.value = validationRut(e.value);
  if (!isValidRUT.value) {
    return (fields.value[0].errors = ['RUT es invalido']);
  } else {
    fields.value[0].state = false;
    delete fields.value[0].errors;
  }
  const rutWithoutSpecialChars = e.value.replace(/[-.]/g, '');
  const { client } = await findClientByTributaryCode(rutWithoutSpecialChars);

  if (client) {
    store.setProp({ key: 'client', value: client });
  }

  if (!client) {
    store.setProp({ key: 'client', value: { rut: e.value } });
  }
};

const fetchDolarChile = async () => {
  try {
    const cotizations = await getDolarChile();
    if (!cotizations || !cotizations.dolar || !cotizations.uf) {
      $toast?.error(
        t(
          'landing.components.form_home.hay_un_error_en_la_cotizacion_de_divisas',
        ),
      );
      return;
    }

    dolarPrice.value = cotizations.dolar;
    UFvalue.value = cotizations.uf;
    store.setProp({
      key: 'pricing',
      value: { dolar: dolarPrice.value, uf: UFvalue.value },
    });

    if (!dolarPrice.value || !UFvalue.value) {
      isButtonDisabled.value = true;
    } else {
      handleSliderChange(Math.round((1000 * dolarPrice.value) / UFvalue.value));
    }
  } catch (error) {
    $toast?.error(
      t(
        'landing.components.form_home.hay_un_error_en_la_cotizacion_de_divisas',
      ),
    );
  }
};

const handleCountrySpecificLogic = async (country: string) => {
  endPrice.value = 0;
  // endPrice.value = calculatePolicyPrice(200000);
};

const fetchAssets = async () => {
  try {
    const assets = await getAssets();
    const fieldAsset = fields.value.find((field: any) => field.key === 'asset');
    fieldAsset.options = assets;
  } catch (error) {
    $toast?.error('Error fetching assets');
  }
};

const checkCorredorId = async () => {
  let params = { corredorId: route.query.corredorId ? String(route.query.corredorId) : '0' };
  try {
    const { corredor } = await CorredorRepository.getCorredor(params);
    store.setProp({
      key: 'corredor',
      value: corredor,
    });
    corredorValid.value = true;
    setFieldsDisabled(false);
  } catch (error) {
    corredorValid.value = false;
    setFieldsDisabled(true);
  } finally {
    corredorChecked.value = true;
  }
};

onMounted(async () => {
  fetchDolarChile()
  subDomainInitial.value = getSubDomainInitial();
  await handleCountrySpecificLogic(subDomainInitial.value);
  await fetchAssets();
  await checkCorredorId();
});
// computed(() => fields.value);

const fields: any = ref([
  {
    key: 'rut',
    title: t('landing.terrestre.contratacion_cl.rut_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.rut_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding text-uppercase',
    preppendClass: 'inputLanding-custom-user bg-white',
    iconLeft: '/img/user.png',
    hidden: false,
    errors: [],
    listenChange: true,
  },
  {
    key: 'email',
    title: t('global.email'),
    type: 'email',
    validations: {
      rules: {
        required: true,
        email: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
        email: t('global.email_invalido'),
      },
    },
    placeholder: t('global.email'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
    preppendClass: 'inputLanding-custom-user bg-white',
    iconLeft: '/img/user.png',
    listenFocus: true,
  },
  {
    key: 'phone',
    title: t('global.telefono'),
    type: 'text',
    validations: {
      rules: {
        required: true,
        max: 12,
        min: 8,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('global.telefono'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
    preppendClass: 'inputLanding-custom-phone bg-white',
    iconLeft: getImageFlag(window.location.origin),
  },
  {
    key: 'asset',
    title: t('landing.components.form_home.tipo_de_mercancia_transportada_contempora'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    selectLabel: null,
    placeholder: t(
      'landing.components.form_home.tipo_de_mercancia_transportada_placeholder',
    ),
    listenSelect: true,
    listenRemove: true,
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: assetOptions.value,
    hidden: false,
  },
  {
    key: 'currency',
    title: t('landing.components.form_home.tipo_moneda'),
    type: 'select',
    value: 2,
    hidden: true,
    selectLabel: null,
    placeholder: t(
      'landing.components.form_home.elige_tipo_moneda',
    ),
    label: 'simbol',
    formatValue: (val: any) => val?.id,
    listenSelect: true,
    listenRemove: true,
    cols: 12,
    customClass: 'inputLanding',
    options: [
      {
        id: 1,
        simbol: 'USD'
      },
      {
        id: 2,
        simbol: 'UF'
      }
    ],
  },
]);

store.setProp(
  {
    key: 'currencyType',
    value: { id: 2, simbol: 'UF' }
  }
);

const setFieldsDisabled = (disabled: boolean) => {
  fields.value.forEach((field: any) => {
    field.disabled = disabled;

    if (field.type === 'select') {
      const classNames = String(field.customClass ?? '')
        .split(' ')
        .filter(
          (className: string) =>
            className && className !== MULTISELECT_DISABLED_CLASS,
        );

      if (disabled) {
        classNames.unshift(MULTISELECT_DISABLED_CLASS);
      }

      field.customClass = classNames.join(' ');
    }
  });
};

const handleOnSubmit = ({ items, isFormValid }: any) => {
  if (!isFormValid || isCorredorUnavailable.value || !isValidRUT.value) return;
  if (!toogleIndemnizationLimit.value) {
    return (toogleIndemnizationLimit.value = true);
  }
  const newAudit = {
    email: items.email,
    phone: items.phone,
    asset: items.asset?.nombre,
  };

  createAudits(newAudit);
  findClient(newAudit.email);

  const searchParams = route.query;
  const dataStore = {
    ...newAudit,
    asset: items.asset,
    subAsset: items.subAsset ?? null,
    product: items.product ?? null,
    indemnificationLimit: Number(indemnificationLimit.value.toFixed(0)),
    endPrice: endPrice.value,
    queryParams: searchParams,
  };

  store.setProp({ key: 'data', value: dataStore });

  router.push({ name: 'Contratacion' });
};

const handleSliderChange = (data: number) => {
  errorResponse.value = null;
  endPrice.value = 0;
  return (indemnificationLimit.value = data);
};

const handleBlur = (data: any) => {
  createAudits({ email: data.value, asset: null, phone: null });
};

const handleChangeSelect = async (e: any) => {
  toogleIndemnizationLimit.value = false;
  endPrice.value = 0;
  errorResponse.value = null;

  const key = e.input.key;

  // --- Cambio en el campo 'asset' ---
  if (key === 'asset') {
    await refForm.value.setValue({ key: 'product', value: null });
  }
  if (key === 'currency') {
    store.setProp({ key: 'currencyType', value: e.value });
  }
};

const handleCalculatePrice = async () => {
  if (isCorredorUnavailable.value) return;

  const { items } = await refForm.value.getValues();

  try {
    const endPriceResponse = await calculatePriceForLanding({
      riskType: items.product ? items.product.riskType : items.asset?.riskType ?? { description: 'PAQUETERIA' },
      indemnificationLimitMax: indemnificationLimit.value,
      assetId: items.asset?.id ?? 999,
    });

    endPrice.value = endPriceResponse.calculation;
  } catch (error: any) {
    errorResponse.value = error.message;
  }
};

const handleRemoveActive = async (e: any) => {
  endPrice.value = 0;
  toogleIndemnizationLimit.value = false;
};

const handleResetForm = async () => {
  await refForm.value.resetValues();
  // store.setProp({ key: 'currencyType', value: { id: null, simbol: null } });
  toogleIndemnizationLimit.value = false;
}
</script>

<template>
  <div class="policy-client-chile__form form">
    <div className="form__container">
      <h3 class="form__title">
        {{ t('landing.components.form_home.cotiza_policy_client_chile') }}
      </h3>
      <Form
        ref="refForm"
        :inputs="fields"
        @onSubmit="handleOnSubmit"
        @blur="handleBlur"
        @select="handleChangeSelect"
        @remove="handleRemoveActive"
        @change="handleChangeInput"
      >
        <template #buttons>
          <div v-if="toogleIndemnizationLimit">
            <h3 class="form__title">
              {{
                t('landing.components.form_home.monto_asegurado_policy_client_chile')
              }}
            </h3>
            <div class="form__limitIndemnization">
              <p class="form__subtitle">
                {{
                  t(
                    'landing.components.form_home.seleccione_el_monto',
                  )
                }}
              </p>
              <div class="form__slider">
                <Slider
                  :subDomainInitial="subDomainInitial"
                  :dolarPrice="dolarPrice ?? undefined"
                  :UFvalue="UFvalue ?? undefined"
                  :currency="store.currencyType"
                  @value="handleSliderChange"
                ></Slider>
              </div>
              <div class="form__endPrice-container">
                <div class="form__endPrice-container d-flex flex-column justify-content-center align-items-center">

                  <p class="form__endPrice" v-if="endPrice !== 0"></p>
                  <p class="form__endPrice">
                    <span>{{ t('landing.components.form_home.precio_final') }}:</span>
                    <span class="form__endPrice-span">{{ getTypeMoney(subDomainInitial) }} {{ endPrice }}</span>
                    +
                    {{t('landing.components.form_home.iva')}}
                  </p>
                  <p v-if="errorResponse" class="text-danger">
                    {{ errorResponse }}
                  </p>
                </div>
              </div>
              <!-- <span class="form__span">{{
                t('landing.components.form_home.valores_en_pesos_chilenos')
                }}</span> -->
            </div>
          </div>
          <p v-if="isCorredorInvalid" class="form__corredor-error">
            {{ t('landing.components.form_home.corredor_consultado_invalido') }}
          </p>
          <div class="d-flex flex-column btn-container-contempora">
            <button v-if="endPrice === 0 && toogleIndemnizationLimit"
              class="p-4 w-100 my-2 px-2 background-green color-white border-0 rounded-4"
              style="font-weight: bold; font-size: 2rem"
              :disabled="isCorredorUnavailable"
              @click="handleCalculatePrice" type="button">
              {{ t('landing.components.form_home.calcular_precio') }}
            </button>
            <button v-if="endPrice !== 0 || !toogleIndemnizationLimit"
              class="p-4 w-100 my-2 px-2 background-green color-white border-0 rounded-4"
              style="font-weight: bold; font-size: 2rem"
              :disabled="isButtonDisabled || (toogleIndemnizationLimit && endPrice === 0) || isCorredorUnavailable"
              type="submit"
            >
              {{ endPrice == 0 
                ? t('landing.components.form_home.cotizar_policy_client_chile')
                : t('landing.components.form_home.contratar_policy_client_chile')
                }}
            </button>
            <button
              class="p-4 w-100 my-2 px-2 background-green color-white border-0 rounded-4"
              style="font-weight: bold; font-size: 2rem"
              type="button"
              :disabled="isCorredorUnavailable"
              @click="handleResetForm"
            >
              {{ t('landing.components.form_home.volver_a_cotizar') }}
            </button>
          </div>
        </template>
      </Form>
    </div>
  </div>
</template>
